import React, { useState, useEffect } from 'react';
import { Form, Modal, Space } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { StyledModal, StyledInput, FormSection, InfoBox, InfoText, PrimaryButton, CancelButton } from './styles';
import CategorySelector from './CategorySelector';
import SchedulesSelector from './SchedulesSelector';
import GoogleMapsAddressInput from './GoogleMapsAddressInput';
import type { StoreFormProps } from './types';
import type { Category, Schedule, CreateStorePayload, UpdateStorePayload } from '../../redux/UserStores';
import { LABELS, MESSAGES, VALIDATION_MESSAGES, PLACEHOLDERS } from './constants';

const StoreForm: React.FC<StoreFormProps> = ({
  store,
  categories,
  schedules,
  loading,
  onSubmit,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedSchedules, setSelectedSchedules] = useState<Schedule[]>([]);
  const [currentStoreData, setCurrentStoreData] = useState({
    name: '',
    address: '',
    streetName: '',
    streetNumber: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
    phoneNumber: '',
    latitude: 0,
    longitude: 0,
  });

  useEffect(() => {
    if (store) {
      form.setFieldsValue({
        name: store.name || '',
        address: store.address || '',
        streetName: store.streetName || '',
        streetNumber: store.streetNumber || '',
        apartment: store.apartment || '',
        city: store.city || '',
        state: store.state || '',
        zipCode: store.zipCode || '',
        phoneNumber: store.phoneNumber || '',
      });
      setCurrentStoreData({
        name: store.name || '',
        address: store.address || '',
        streetName: store.streetName || '',
        streetNumber: store.streetNumber || '',
        apartment: store.apartment || '',
        city: store.city || '',
        state: store.state || '',
        zipCode: store.zipCode || '',
        phoneNumber: store.phoneNumber || '',
        latitude: store.latitude || 0,
        longitude: store.longitude || 0,
      });
      setSelectedCategories(store.categories || []);
      setSelectedSchedules(store.schedules || []);
    } else {
      form.resetFields();
      setCurrentStoreData({
        name: '',
        address: '',
        streetName: '',
        streetNumber: '',
        apartment: '',
        city: '',
        state: '',
        zipCode: '',
        phoneNumber: '',
        latitude: 0,
        longitude: 0,
      });
      setSelectedCategories([]);
      setSelectedSchedules([]);
    }
  }, [store, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (selectedCategories.length === 0) {
        Modal.error({
          title: 'Validation Error',
          content: VALIDATION_MESSAGES.CATEGORY_REQUIRED,
        });
        return;
      }

      if (selectedSchedules.length === 0) {
        Modal.error({
          title: 'Validation Error',
          content: VALIDATION_MESSAGES.SCHEDULE_REQUIRED,
        });
        return;
      }

      const categoryIds = selectedCategories.map((cat) => cat.id);
      const scheduleIds = selectedSchedules.map((sch) => sch.id);

      const payload: CreateStorePayload | UpdateStorePayload = {
        ...(store ? { id: store.id } : {}),
        name: values.name.trim(),
        address: currentStoreData.address?.trim() || '',
        city: currentStoreData.city?.trim() || '',
        phoneNumber: currentStoreData.phoneNumber?.trim() || '',
        streetName: currentStoreData.streetName?.trim() || '',
        streetNumber: currentStoreData.streetNumber?.trim() || '',
        apartment: currentStoreData.apartment?.trim() || '',
        state: currentStoreData.state?.trim() || '',
        zipCode: currentStoreData.zipCode?.trim() || '',
        categories: categoryIds,
        schedules: scheduleIds,
        latitude: currentStoreData.latitude || 0,
        longitude: currentStoreData.longitude || 0,
      };

      onSubmit(payload);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  return (
    <StyledModal
      title={
        <Space>
          <span>🏪</span>
          <span>{store ? LABELS.UPDATE_STORE : LABELS.CREATE_STORE}</span>
        </Space>
      }
      open={true}
      onCancel={onCancel}
      footer={[
        <CancelButton key="cancel" onClick={onCancel} disabled={loading}>
          {LABELS.CANCEL}
        </CancelButton>,
        <PrimaryButton
          key="submit"
          type="primary"
          icon={<CheckOutlined />}
          onClick={handleSubmit}
          loading={loading}
        >
          {store ? LABELS.UPDATE : LABELS.CREATE}
        </PrimaryButton>,
      ]}
      width={800}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <FormSection>
          <Form.Item
            name="name"
            label={LABELS.STORE_NAME}
            rules={[{ required: true, message: VALIDATION_MESSAGES.STORE_NAME_REQUIRED }]}
          >
            <StyledInput placeholder={PLACEHOLDERS.STORE_NAME} />
          </Form.Item>

          <Form.Item label={LABELS.STORE_ADDRESS}>
            <GoogleMapsAddressInput
              currentStore={currentStoreData}
              setCurrentStore={setCurrentStoreData}
            />
          </Form.Item>

          <CategorySelector
            categories={categories}
            selectedCategories={selectedCategories}
            onCategoryChange={setSelectedCategories}
          />

          <SchedulesSelector
            schedules={schedules}
            selectedSchedules={selectedSchedules}
            onScheduleChange={setSelectedSchedules}
          />

          <InfoBox>
            <InfoText>{MESSAGES.REQUIRED_FIELDS_NOTE}</InfoText>
          </InfoBox>
        </FormSection>
      </Form>
    </StyledModal>
  );
};

export default React.memo(StoreForm);
